/** @jsxImportSource @emotion/react */
import React from 'react'
import { View } from '@components/atoms'
import { Modal, ModalInfoType } from '@components/molecules'
import DaumPostcode from 'react-daum-postcode'

import {
	useAppSelector,
	useAppDispatch,
	paymentActions,
	PaymentDeliveryType,
	PaymentStateType,
} from '@redux/index'
import { unsupportedPayPalCountries } from '@redux/store/home/payment.store'
import { produce } from 'immer'
import { Country, ICountry, State, IState } from 'country-state-city'

import { ChevronDown } from './ChevronDown'

/**
 * 결제 화면의 Delivery 블록 — Monolith(2안). 구 구현은 `systems/payment/PaymentDelivery.tsx`.
 *
 * 배송지 로직은 그대로다 — 국가 변경 시 하위 필드 초기화, PayPal 미지원 국가면 카드로 되돌리고
 * 모달, 한국이면 다음 우편번호 검색으로 갈아타기, `country-state-city` 의 주(state) 목록.
 * 바꾼 것은 입력 껍데기뿐이다: MUI Select/TextField 외형 대신 밑줄 입력과 네이티브 셀렉트.
 *
 * 구 화면의 국가 셀렉트에는 타이핑으로 항목을 찾는 직접 구현(`handleCountryKeyDown`)이 붙어
 * 있었는데, 네이티브 셀렉트가 같은 일을 브라우저 기본 기능으로 한다 — 그래서 뺐다.
 */
export const MonolithPaymentDelivery = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const paymentMethod: string = useAppSelector(state => state.payment.paymentMethod)
	const delivery: PaymentDeliveryType = useAppSelector(
		state => state.payment.paymentDelivery,
	)
	const errorMessages: PaymentStateType['paymentErrorMessages'] = useAppSelector(
		state => state.payment.paymentErrorMessages,
	)

	// state
	const [modalInfo, setModalInfo] = React.useState<ModalInfoType>({
		target: '',
		title: '',
	})
	const [showModal, setShowModal] = React.useState<boolean>(false)

	const countries: ICountry[] = React.useMemo(() => {
		const res: ICountry[] = Country.getAllCountries()
		res.sort((a, b) => {
			if (a.name > b.name) {
				return 1
			} else if (a.name < b.name) {
				return -1
			}

			return 0
		})
		return res
	}, [])

	const states: IState[] = React.useMemo(() => {
		const res: IState[] =
			delivery.countryIsoCode === ''
				? []
				: State.getStatesOfCountry(delivery.countryIsoCode)

		return res
	}, [delivery.countryIsoCode])

	const update = (patch: Partial<PaymentDeliveryType>) => {
		const newDelivery = produce(delivery, draft => {
			Object.assign(draft, patch)
		})

		reduxDispatch(
			paymentActions.PAYMENT_DELIVERY({
				...newDelivery,
			}),
		)
	}

	const onCountryChange = (countryName: string) => {
		const thisCountry = countries.filter(aCountry => aCountry.name === countryName)[0]

		if (thisCountry === undefined) return

		if (
			paymentMethod === 'paypal' &&
			unsupportedPayPalCountries.includes(thisCountry.isoCode)
		) {
			reduxDispatch(paymentActions.PAYMENT_METHOD('credit'))

			reduxDispatch(
				paymentActions.MODAL_INFO({
					target: 'paypalNotAllowed',
					title: 'PayPal Unavailable',
				}),
			)
			reduxDispatch(paymentActions.SHOW_MODAL(true))
		}

		update({
			countryName: countryName,
			countryIsoCode: thisCountry.isoCode,
			stateName: '',
			stateIsoCode: '',
			city: '',
			street1: '',
			zip: '',
		})
	}

	const onStateSelectChange = (stateName: string) => {
		const thisState = states.filter(aState => aState.name === stateName)[0]

		update({
			stateName: stateName,
			stateIsoCode: thisState === undefined ? '' : thisState.isoCode,
		})
	}

	const handleComplete = (data: any) => {
		setShowModal(false)

		const roadAddressArr = data.roadAddress.split(`${data.sido} ${data.sigungu} `)
		const roadAddress = roadAddressArr.length > 1 ? roadAddressArr[1] : roadAddressArr[0]

		update({
			zip: data.zonecode,
			stateIsoCode: data.sidoEnglish,
			stateName: data.sido,
			city: data.sigungu,
			street1: roadAddress,
		})
	}

	const onKrAddressAutoCompleteClick = () => {
		setModalInfo({
			target: 'daumAddress',
			title: '주소 검색',
		})
		setShowModal(true)
	}

	const onModalSubmit = () => {}

	return (
		<View className="mnPaySection">
			<View className="mnPaySectionHead">
				<span className="mnKicker">Delivery</span>
			</View>

			<View className="mnPaySectionBody">
				<View className="mnField">
					<label className="mnLabel" htmlFor="mnPayCountry">
						Country
					</label>
					<View className="mnPaySelectWrap">
						<select
							id="mnPayCountry"
							className={`mnInput mnPaySelect ${
								errorMessages.delivery.countryName !== '' ? 'error' : ''
							}`}
							value={delivery.countryName}
							data-empty={delivery.countryName === '' ? 'true' : 'false'}
							onChange={e => onCountryChange(e.target.value)}
						>
							<option value="" disabled>
								Choose a country
							</option>
							{countries.map((aCountry: ICountry) => (
								<option key={aCountry.isoCode} value={aCountry.name}>
									{aCountry.name}
								</option>
							))}
						</select>
						<ChevronDown />
					</View>
					{errorMessages.delivery.countryName !== '' && (
						<span className="mnError">{errorMessages.delivery.countryName}</span>
					)}
				</View>

				{delivery.countryIsoCode === 'KR' ? (
					<>
						<View className="mnField">
							<label className="mnLabel" htmlFor="mnPayKrAddress">
								주소
							</label>
							<View className="mnPayCoupon">
								<View className="mnField mnPayCouponField">
									<input
										id="mnPayKrAddress"
										type="text"
										readOnly={true}
										className="mnInput"
										value={`${delivery.stateName} ${delivery.city} ${delivery.street1}`}
										onClick={onKrAddressAutoCompleteClick}
									/>
								</View>
								<button
									type="button"
									className="mnCtaGhost mnPayCouponApply"
									onClick={onKrAddressAutoCompleteClick}
								>
									주소 검색
								</button>
							</View>
						</View>

						<View className="mnField">
							<label className="mnLabel" htmlFor="mnPayKrStreet2">
								상세 주소
							</label>
							<input
								id="mnPayKrStreet2"
								type="text"
								className="mnInput"
								value={delivery.street2}
								onChange={e => update({ street2: e.target.value })}
							/>
						</View>

						<View className="mnField">
							<label className="mnLabel" htmlFor="mnPayKrZip">
								우편번호
							</label>
							<input
								id="mnPayKrZip"
								type="text"
								readOnly={true}
								className="mnInput"
								value={delivery.zip}
							/>
						</View>
					</>
				) : (
					<>
						<View className="mnField">
							<label className="mnLabel" htmlFor="mnPayZip">
								ZIP / Postal code
							</label>
							<input
								id="mnPayZip"
								type="text"
								autoComplete="postal-code"
								className={`mnInput ${errorMessages.delivery.zip !== '' ? 'error' : ''}`}
								value={delivery.zip}
								onChange={e => update({ zip: e.target.value })}
							/>
							{errorMessages.delivery.zip !== '' && (
								<span className="mnError">{errorMessages.delivery.zip}</span>
							)}
						</View>

						<View className="mnField">
							<label className="mnLabel" htmlFor="mnPayStreet1">
								Street 1
							</label>
							<input
								id="mnPayStreet1"
								type="text"
								autoComplete="address-line1"
								className={`mnInput ${
									errorMessages.delivery.street1 !== '' ? 'error' : ''
								}`}
								value={delivery.street1}
								onChange={e => update({ street1: e.target.value })}
							/>
							{errorMessages.delivery.street1 !== '' && (
								<span className="mnError">{errorMessages.delivery.street1}</span>
							)}
						</View>

						<View className="mnField">
							<label className="mnLabel" htmlFor="mnPayStreet2">
								Street 2
							</label>
							<input
								id="mnPayStreet2"
								type="text"
								autoComplete="address-line2"
								className="mnInput"
								value={delivery.street2}
								onChange={e => update({ street2: e.target.value })}
							/>
						</View>

						<View className="mnField">
							<label className="mnLabel" htmlFor="mnPayCity">
								City
							</label>
							<input
								id="mnPayCity"
								type="text"
								autoComplete="address-level2"
								className={`mnInput ${errorMessages.delivery.city !== '' ? 'error' : ''}`}
								value={delivery.city}
								onChange={e => update({ city: e.target.value })}
							/>
							{errorMessages.delivery.city !== '' && (
								<span className="mnError">{errorMessages.delivery.city}</span>
							)}
						</View>

						<View className="mnField">
							<label className="mnLabel" htmlFor="mnPayState">
								State / Province / Region
							</label>
							{states.length === 0 ? (
								<input
									id="mnPayState"
									type="text"
									autoComplete="address-level1"
									className={`mnInput ${
										errorMessages.delivery.states !== '' ? 'error' : ''
									}`}
									value={delivery.stateName}
									onChange={e => update({ stateName: e.target.value })}
								/>
							) : (
								<View className="mnPaySelectWrap">
									<select
										id="mnPayState"
										className={`mnInput mnPaySelect ${
											errorMessages.delivery.states !== '' ? 'error' : ''
										}`}
										value={delivery.stateName}
										data-empty={delivery.stateName === '' ? 'true' : 'false'}
										onChange={e => onStateSelectChange(e.target.value)}
									>
										<option value="" disabled>
											Choose a region
										</option>
										{states.map((aState: IState) => (
											<option key={aState.isoCode} value={aState.name}>
												{aState.name}
											</option>
										))}
									</select>
									<ChevronDown />
								</View>
							)}
							{errorMessages.delivery.states !== '' && (
								<span className="mnError">{errorMessages.delivery.states}</span>
							)}
						</View>
					</>
				)}
			</View>

			<Modal
				target={modalInfo.target}
				title={modalInfo.title}
				show={showModal}
				onCloseModal={() => {
					setShowModal(false)
				}}
				closeStatus="inherit"
				showSubmit={false}
				showClose={false}
				onSubmit={onModalSubmit}
				rootStyle={{
					maxWidth: '70rem',
				}}
			>
				<View css={{ width: '68rem', maxWidth: '80vw' }}>
					<DaumPostcode onComplete={handleComplete} />
				</View>
			</Modal>
		</View>
	)
}
